import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, Users, Zap, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a0a2e] text-white py-24">
      <div className="max-w-6xl mx-auto px-6">
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="mb-8 border-cyan-500/50 hover:border-cyan-400 bg-transparent text-white"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Home
        </Button>

        <div className="text-center mb-16">
          <h1 className="text-4xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-pink-500 bg-clip-text text-transparent">
            About StreamShares
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Revolutionizing the creator economy by connecting creators with their biggest supporters through transparent revenue sharing.
          </p>
        </div>

        <div className="space-y-16">
          {/* Mission Section */}
          <section>
            <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              We believe every creator deserves sustainable income and every fan should have the opportunity to invest in the creators they love. StreamShares is building the infrastructure that makes the creator economy fair, transparent, and dispute-free for everyone involved.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl border border-cyan-500/30">
                <Target className="w-12 h-12 text-cyan-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">For Creators</h3>
                <p className="text-gray-300">
                  Access sustainable funding from your community while protecting your content and maintaining full creative control.
                </p>
              </div>
              <div className="p-6 bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-xl border border-pink-500/30">
                <Heart className="w-12 h-12 text-pink-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">For Fans</h3>
                <p className="text-gray-300">
                  Become an invested partner in your favorite creator's success and share in their revenue as they grow.
                </p>
              </div>
            </div>
          </section>

          {/* Story Section */}
          <section className="p-8 bg-gradient-to-br from-gray-900/80 to-gray-800/80 rounded-2xl border border-gray-700/50">
            <h2 className="text-3xl font-bold text-white mb-6">Our Story</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>
                StreamShares was founded in 2025 with a simple observation: creators were facing two critical challenges. First, they lacked access to sustainable funding that didn't compromise their creative freedom. Second, they were vulnerable to content appearance disputes that could derail their entire careers.
              </p>
              <p>
                At the same time, we noticed that passionate fans wanted more than just to watch content—they wanted to be part of their favorite creator's journey. But there was no structured way for this to happen.
              </p>
              <p>
                We built StreamShares to solve both problems. By creating a platform where fans can invest in creators and share in their revenue, we're enabling sustainable creator income while building deeper community connections. And by providing tools to manage content appearances and prevent disputes, we're protecting creators from the risks that have plagued the industry.
              </p>
            </div>
          </section>

          {/* Values Section */}
          <section>
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Our Values</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Zap,
                  title: "Innovation",
                  description: "We're building something that's never existed before—a fair, transparent system for the creator economy.",
                  color: "from-cyan-500/20 to-blue-500/20 border-cyan-500/30"
                },
                {
                  icon: Users,
                  title: "Community First",
                  description: "Every decision we make prioritizes the needs of creators and their communities.",
                  color: "from-purple-500/20 to-pink-500/20 border-purple-500/30"
                },
                {
                  icon: Heart,
                  title: "Transparency",
                  description: "We believe in complete openness about how revenue sharing works and how the platform operates.",
                  color: "from-pink-500/20 to-red-500/20 border-pink-500/30"
                }
              ].map((value, index) => {
                const Icon = value.icon;
                return (
                  <div key={index} className={`p-6 bg-gradient-to-br ${value.color} rounded-xl border backdrop-blur-sm hover:scale-105 transition-transform`}>
                    <Icon className="w-10 h-10 text-cyan-400 mb-4" />
                    <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                    <p className="text-gray-300">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Team Section */}
          <section>
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Join Our Journey</h2>
            <p className="text-lg text-gray-300 text-center max-w-3xl mx-auto mb-8">
              We're a passionate team of creators, developers, and finance experts working together to transform the creator economy. Interested in joining us?
            </p>
            <div className="flex justify-center gap-4">
              <Button
                onClick={() => navigate('/careers')}
                className="bg-gradient-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600 text-white px-8 py-6 border-0"
              >
                View Open Positions
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-2 border-cyan-500/50 hover:border-cyan-400 bg-transparent text-white px-8 py-6"
              >
                Join Waitlist
              </Button>
            </div>
          </section>

          {/* Contact Section */}
          <section className="p-8 bg-gradient-to-r from-cyan-500/10 to-pink-500/10 rounded-2xl border border-cyan-500/30 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Get in Touch</h2>
            <p className="text-gray-300 mb-6">
              Have questions or want to learn more? We'd love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 text-gray-300">
              <div>
                <strong className="text-cyan-400">Email:</strong> info@streamshares.co.uk
              </div>
              <div className="hidden sm:block">|</div>
              <div>
                <strong className="text-pink-400">Location:</strong> London, United Kingdom
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
